import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { baseUrl } from '../Utils'; // Assuming baseUrl is an absolute URL ending with '/'

interface AppConfig {
    currency: string;
    logo: string | null;
}

interface ConfigContextType {
    config: AppConfig;
    isLoadingConfig: boolean;
    refreshConfig: () => Promise<void>;
}

const defaultConfig: AppConfig = {
    currency: '₹',
    logo: null,
};

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const ConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [config, setConfig] = useState<AppConfig>(defaultConfig);
    const [isLoadingConfig, setIsLoadingConfig] = useState(true);

    const fetchConfig = async () => {
        try {
            // Using GET as defined in api.php (outside of auth layer)
            const response = await axios.get(`${baseUrl}settings/public`);

            if (response.data) {
                setConfig({
                    currency: response.data.currency || '₹',
                    logo: response.data.logo || null,
                });
            }
        } catch (error) {
            console.error('Failed to fetch public settings from backend:', error);
            // On failure, fall back to default
            setConfig(defaultConfig);
        } finally {
            setIsLoadingConfig(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    return (
        <ConfigContext.Provider value={{ config, isLoadingConfig, refreshConfig: fetchConfig }}>
            {children}
        </ConfigContext.Provider>
    );
};

export const useConfig = () => {
    const context = useContext(ConfigContext);
    if (context === undefined) {
        throw new Error('useConfig must be used within a ConfigProvider');
    }
    return context;
};
